import React, { useEffect, useState } from 'react';
import { Input, Button, MenuProps } from 'antd';
import {
  CaretDownOutlined,
  CheckSquareFilled,
  CloseOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ItemType } from 'antd/es/menu/interface';
import { Filter, FilterOption } from '../../Modal/TaskFiltersModal';
import { User } from '../../../types/User';
import { UserAvatar } from '../../TaskTable/helpers/helper';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import BaseDropdown from '../../UI/Dropdown/BaseDropdown';

interface OwnerDropdownProps {
  filter: Filter;
  onSave: (ownerFilter: Filter) => void;
  onClear: () => void;
}

const OwnerDropdown: React.FC<OwnerDropdownProps> = ({ filter, onSave, onClear }) => {
  const [menuItems, setMenuItems] = useState<ItemType[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [filteredItems, setFilteredItems] = useState(menuItems);
  const [isOpen, setIsOpen] = useState(false);

  const selectedOptions = filter?.options.filter((option) => option.selected) || [];

  useEffect(() => {
    const items = getMenuItems(filter, handleSelectOption);
    setMenuItems(items);
    setFilteredItems(items);
  }, [filter, filter?.options]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);
    const filtered = menuItems.filter((item) => item.name?.toLowerCase().includes(value));
    setFilteredItems(filtered);
  };

  const handleSelectOption = (option: FilterOption) => {
    const ownerFilter = filter;
    const isMeOption = option.name === 'Me';
    ownerFilter.options = ownerFilter.options.map((currOption) => {
      if (isMeOption && currOption.name === 'Me') {
        return {
          ...currOption,
          selected: !currOption.selected,
        };
      } else if (
        !isMeOption && currOption.metadata?.id && option.metadata?.id
          ? currOption.metadata.id === option.metadata.id
          : currOption.name === option.name
      ) {
        return {
          ...currOption,
          selected: !currOption.selected,
        };
      }
      return {
        ...currOption,
        selected: currOption.selected,
      };
    });
    onSave(ownerFilter);
  };

  const searchMenuItem: ItemType = {
    key: 'search',
    label: (
      <div onClick={(e) => e.stopPropagation()}>
        <Input
          prefix={<SearchOutlined className="text-neutral-900" />}
          placeholder={'Search for member'}
          value={searchValue}
          onChange={handleSearch}
          className="mb-1 text-neutral-900"
        />
      </div>
    ),
  };

  const menu: MenuProps = {
    items: [searchMenuItem, ...filteredItems],
  };

  return (
    <BaseDropdown
      disableTransition
      menu={menu}
      trigger={['click']}
      open={isOpen}
      onOpenChange={(open, info) => {
        if (!open && (info as { source?: string })?.source === 'menu') return;
        setIsOpen(open);
      }}
    >
      <Button type="default" className="pl-2 pr-2 text-neutral-900">
        {selectedOwnersView(filter)}
        {selectedOptions.length === 0 && <CaretDownOutlined />}
        {selectedOptions.length > 0 && (
          <CustomTooltip title="Clear">
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            />
          </CustomTooltip>
        )}
      </Button>
    </BaseDropdown>
  );
};

function getMenuItems(filter: Filter, onSelect: (option: FilterOption) => void): ItemType[] {
  if (!filter || !filter.options) {
    return [];
  }
  const menuItems = filter.options.map((userOption) => {
    const selected = userOption.selected;
    const user: User | undefined = userOption.metadata;
    const isMeOption = userOption.name === 'Me';
    return {
      key: isMeOption ? userOption.name : user?.id,
      name: isMeOption ? userOption.name : user ? user?.fullName || user?.email : 'Unassigned',
      label: (
        <div
          className="flex items-center justify-start gap-2  px-1 rounded text-sm font-normal cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(userOption);
          }}
        >
          {!selected && (
            <div className="w-[14px] h-[14px] rounded-sm border-box border border-solid border-neutral-600" />
          )}
          {selected && <CheckSquareFilled style={{ fontSize: '16px' }} className="text-blue-500" />}
          <UserAvatar assignee={user?.id} usersWithAccessToSpace={[{ User: user, userId: user?.id }]} />

          <div className="h-7 max-w-[250px] truncate text-brand-grey-dark text-sm font-normal leading-7">
            {isMeOption ? userOption.name : user ? user?.fullName || user?.email : 'Unassigned'}
          </div>
        </div>
      ),
      user,
      isMeOption,
    };
  });

  // Sort A→Z: first by name, if no name then by email
  menuItems.sort((a, b) => {
    // Keep "Me" option at the top
    if (a.isMeOption && !b.isMeOption) return -1;
    if (!a.isMeOption && b.isMeOption) return 1;
    if (a.isMeOption && b.isMeOption) return 0;

    const userA = a.user;
    const userB = b.user;

    // Handle "Unassigned" case
    if (!userA && !userB) return 0;
    if (!userA) return 1;
    if (!userB) return -1;

    // Sort by name first, then by email
    const nameA = userA.fullName || userA.email || '';
    const nameB = userB.fullName || userB.email || '';

    return nameA.localeCompare(nameB);
  });

  return menuItems;
}

const selectedOwnersView = (filter: Filter) => {
  const allOwnersView = (
    <div className="flex items-center">
      <UserOutlined size={20} className="min-w-[20px] ml-1" />
      <span className="truncate text-sm font-normal leading-tight">Owner</span>
    </div>
  );
  if (!filter || !filter.options) {
    return allOwnersView;
  }
  const selectedOptions = filter.options
    .filter((option) => option.selected)
    .sort((a, b) => {
      if (a.name === 'Me' && !(b.name === 'Me')) return -1;
      if (!(a.name === 'Me') && b.name === 'Me') return 1;
      if (a.name === 'Me' && b.name === 'Me') return 0;

      const userA = a.metadata as User;
      const userB = b.metadata as User;

      // Handle "Unassigned" case
      if (!userA && !userB) return 0;
      if (!userA) return 1;
      if (!userB) return -1;

      // Sort by name first, then by email
      const nameA = userA.fullName || userA.email || '';
      const nameB = userB.fullName || userB.email || '';

      return nameA.localeCompare(nameB);
    });
  if (selectedOptions.length === 0) {
    return allOwnersView;
  }

  return (
    <div className="flex items-center">
      {selectedOptions.length > 1 && <TeamOutlined size={20} className=" min-w-[20px] mr-1 ml-1" />}
      {selectedOptions.length === 1 && <UserOutlined size={20} className=" min-w-[20px] mr-1 ml-1" />}
      <div className="flex items-center">
        {selectedOptions.slice(0, 4).map((option, index) => {
          return (
            <div
              key={option.metadata?.id || option.name}
              className={`${index > 0 ? '-ml-1.5' : ''}`}
              style={{ zIndex: selectedOptions.slice(0, 4).length - index }}
            >
              <UserAvatar
                assignee={option.metadata?.id}
                usersWithAccessToSpace={[{ User: option.metadata, userId: option.metadata?.id }]}
              />
            </div>
          );
        })}
        {selectedOptions.length > 4 && (
          <div
            className="w-6 h-6 bg-neutral-300 rounded-full flex items-center justify-center -ml-1.5 border border-white"
            style={{ zIndex: 0 }}
          >
            <span className="text-neutral-900 text-xs font-semibold">+{selectedOptions.length - 4}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerDropdown;
